import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';

import {
  createCombineCondition,
  isCombineCondition,
} from '@/types/advanceFilter';

export const toJsonText = function (query: Condition): string {
  if (!query) {
    return '';
  }

  return JSON.stringify(query, (key, value) => {
    if (isCombineCondition(value) && !value.conditions?.length) {
      return undefined;
    }
    return value;
  });
};

const escapeLuceneValue = (value: string): string => {
  if (/[\s+\-!(){}[\]^"~*?:\\/&|]/.test(value)) {
    return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return value;
};

const escapeLuceneWildcard = (value: string): string =>
  value.replace(/([\s+\-!(){}[\]^"~*?:\\/&|])/g, '\\$1');

const conditionToLucene = (condition: Condition | undefined): string => {
  if (!condition) {
    return '';
  }

  if (isCombineCondition(condition)) {
    const subQueries = (condition.conditions || [])
      .map(conditionToLucene)
      .filter(Boolean);

    if (subQueries.length === 0) {
      return '';
    }
    if (subQueries.length === 1) {
      return subQueries[0];
    }

    const operator =
      condition.operator === ConditionOperatorEnum.Or ? 'OR' : 'AND';
    const parts =
      operator === 'AND'
        ? subQueries.map((q) => (q.includes(' OR ') ? `(${q})` : q))
        : subQueries;
    return parts.join(` ${operator} `);
  }

  const { property, value: values } = condition;
  if (!property || !values?.length) {
    return '';
  }

  switch (condition.operator) {
    case ConditionOperatorEnum.In:
    case ConditionOperatorEnum.Equals: {
      return values
        .map((v) => `${property}:${escapeLuceneValue(v)}`)
        .join(' OR ');
    }

    case ConditionOperatorEnum.NotIn:
    case ConditionOperatorEnum.NotEquals: {
      if (values.length === 1) {
        return `NOT ${property}:${escapeLuceneValue(values[0])}`;
      }
      return `NOT ${property}:(${values.map(escapeLuceneValue).join(' OR ')})`;
    }

    case ConditionOperatorEnum.Contains:
    case ConditionOperatorEnum.ContainsAnyOf: {
      const expr = values
        .map((v) => `${property}:*${escapeLuceneWildcard(v)}*`)
        .join(' OR ');
      return values.length > 1 ? `(${expr})` : expr;
    }

    case ConditionOperatorEnum.NotContains:
    case ConditionOperatorEnum.ContainsNoneOf: {
      return values
        .map((v) => `NOT ${property}:*${escapeLuceneWildcard(v)}*`)
        .join(' AND ');
    }

    default:
      return '';
  }
};

const collectProperties = (condition: Condition | undefined): string[] => {
  if (!condition) {
    return [];
  }
  if (isCombineCondition(condition)) {
    return (condition.conditions || []).flatMap(collectProperties);
  }
  return condition.property ? [condition.property] : [];
};

const stripPropertyTerms = (filter: string, properties: string[]): string => {
  let result = filter;
  for (const prop of properties) {
    const escaped = RegExp.escape(prop);
    result = result.replace(
      new RegExp(`(NOT\\s+)?${escaped}:(\\([^)]*\\)|"[^"]*"|[^\\s)]+)`, 'g'),
      ''
    );
  }
  let cleaned = result;
  let prev;
  do {
    prev = cleaned;
    cleaned = cleaned
      .replace(/\(\s*(AND|OR)?\s*\)/g, '')
      .replace(/\s+(AND|OR)\s+(AND|OR)\s+/g, ' $1 ')
      .replace(/^\s*(AND|OR)\s+/, '')
      .replace(/\s+(AND|OR)\s*$/, '');
  } while (cleaned !== prev);
  return cleaned.trim();
};

const extractPropertyValues = (filter: string, property: string): string[] => {
  const escaped = RegExp.escape(property);
  const values: string[] = [];

  const groupedPattern = new RegExp(`(?<!NOT\\s)${escaped}:\\(([^)]*)\\)`, 'g');
  let match;
  while ((match = groupedPattern.exec(filter)) !== null) {
    match[1]
      .split(/\s+OR\s+/)
      .map((v) => v.replace(/^"|"$/g, '').trim())
      .filter(Boolean)
      .forEach((v) => values.push(v));
  }

  const singlePattern = new RegExp(
    `(?<!NOT\\s)${escaped}:(?!\\()("([^"]*)"|([^\\s)]+))`,
    'g'
  );
  while ((match = singlePattern.exec(filter)) !== null) {
    const val = match[2] ?? match[3];
    if (val) {
      values.push(val);
    }
  }

  return values;
};

export const fromLucene = (
  filter: string,
  managedProperties: string[]
): Condition => {
  const conditions = managedProperties.reduce<Condition[]>((acc, prop) => {
    const values = extractPropertyValues(filter, prop);
    if (values.length) {
      acc.push({
        property: prop,
        operator: ConditionOperatorEnum.In,
        value: values,
      });
    }
    return acc;
  }, []);

  return createCombineCondition(conditions);
};

export const toLucene = (
  condition: Condition | undefined,
  existingFilter?: string,
  managedProperties?: string[]
): string => {
  const generated = conditionToLucene(condition);

  if (!existingFilter?.trim()) {
    return generated;
  }

  const properties = managedProperties?.length
    ? managedProperties
    : [...new Set(collectProperties(condition))];

  const cleanedExisting = properties.length
    ? stripPropertyTerms(existingFilter, properties)
    : existingFilter.trim();

  if (!generated && !cleanedExisting) {
    return '';
  }
  if (!generated) {
    return cleanedExisting;
  }
  if (!cleanedExisting) {
    return generated;
  }
  return `${cleanedExisting} AND ${generated}`;
};
